import ApiService from '../ApiService';
import * as db from './db';

export const ROUTE_INDEX_KEY = 'route_index';

// ─── Key helpers ──────────────────────────────────────────────────────────────

export const storyKey       = (ref, lang) => `place_${ref}_${lang}_story`;
export const audioKey       = (ref, lang) => `place_${ref}_${lang}_audio`;
export const photoKey       = (ref)       => `place_${ref}_photo`;
export const routeKey       = (id)        => `route_${id}`;
export const userProfileKey = (uid)       => `user_profile_${uid}`;

// Bounding box [minLng, minLat, maxLng, maxLat] for a route's places, with a
// small buffer so the offline map covers the area around each waypoint.
const BBOX_BUFFER_DEG = 0.02; // ~2.2 km

const ASSET_FETCH_CONCURRENCY = 5;

function computeBoundingBox(places) {
  if (!places?.length) return null;
  let minLat =  90, minLng =  180, maxLat = -90, maxLng = -180;
  for (const p of places) {
    if (!p.location) continue;
    const { latitude: lat, longitude: lng } = p.location;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  return [minLng - BBOX_BUFFER_DEG, minLat - BBOX_BUFFER_DEG, maxLng + BBOX_BUFFER_DEG, maxLat + BBOX_BUFFER_DEG];
}

// ─── Pre-cache a full route ───────────────────────────────────────────────────

export async function preCacheRoute({ id, name, route, places, user, language, originLabel, destLabel, onProgress }) {
  // 3 assets per place (story, audio, photo) — counter ticks once per asset.
  const total = places.length * 3;
  let done = 0;
  let totalSizeBytes = 0;

  const tick = () => {
    done++;
    onProgress?.(done, total);
  };

  // Save route data + place list
  await db.set(routeKey(id), { id, name, route, places, language, originLabel, destLabel, cachedAt: Date.now() });

  const fetchAndCacheStory = async (place) => {
    const ref = place.reference;
    try {
      const existing = await db.get(storyKey(ref, language));
      if (!existing) {
        const story = await ApiService.post('/place/story/', {
          place_id: place.id,
          place_ref: ref,
          language,
        }, user);
        if (story) {
          await db.set(storyKey(ref, language), story);
          totalSizeBytes += new Blob([JSON.stringify(story)]).size;
        }
      } else {
        totalSizeBytes += new Blob([JSON.stringify(existing)]).size;
      }
    } catch (e) {
      console.warn(`Cache: story fetch failed for ${ref}`, e);
    }
  };

  const fetchAndCacheAudio = async (place) => {
    const ref = place.reference;
    try {
      const existing = await db.get(audioKey(ref, language));
      if (!existing) {
        const audioBlob = await ApiService.post('/place/audio/', {
          place_id: place.id,
          place_ref: ref,
          language,
        }, user, 'blob');
        if (audioBlob) {
          await db.set(audioKey(ref, language), audioBlob);
          totalSizeBytes += audioBlob.size;
        }
      } else {
        totalSizeBytes += existing.size ?? 0;
      }
    } catch (e) {
      console.warn(`Cache: audio fetch failed for ${ref}`, e);
    }
  };

  const fetchAndCachePhoto = async (place) => {
    const ref = place.reference;
    try {
      const existing = await db.get(photoKey(ref));
      if (!existing) {
        const token = await user.getIdToken(false);
        const resp = await fetch(`${ApiService.apiHostName}/place/${ref}/photo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const blob = await resp.blob();
          await db.set(photoKey(ref), blob);
          totalSizeBytes += blob.size;
        }
      } else {
        totalSizeBytes += existing.size ?? 0;
      }
    } catch (e) {
      console.warn(`Cache: photo fetch failed for ${ref}`, e);
    }
  };

  // Two pool tasks per place:
  //   1. Story → audio (chained; audio depends on story being generated server-side first).
  //   2. Photo (independent).
  // The pool runs up to ASSET_FETCH_CONCURRENCY at a time across all places.
  const tasks = [];
  for (const place of places) {
    tasks.push(async () => {
      await fetchAndCacheStory(place);
      tick();
      await fetchAndCacheAudio(place);
      tick();
    });
    tasks.push(async () => {
      await fetchAndCachePhoto(place);
      tick();
    });
  }

  // Progress is driven by `tick` inside the tasks, not by the pool's post-task hook.
  await runConcurrent(tasks, ASSET_FETCH_CONCURRENCY, () => {});

  await _updateIndex({
    id,
    name,
    originLabel,
    destLabel,
    placesCount: places.length,
    durationText: route?.static_duration_text ?? null,
    distanceText: route?.distance_text ?? null,
    sizeBytes: totalSizeBytes,
    bbox: computeBoundingBox(places),
    cachedAt: Date.now(),
  });
}

// ─── Route index helpers ──────────────────────────────────────────────────────

async function _updateIndex(entry) {
  const index = (await db.get(ROUTE_INDEX_KEY)) || [];
  const filtered = index.filter(r => r.id !== entry.id);
  filtered.push(entry);
  await db.set(ROUTE_INDEX_KEY, filtered);
}

export async function getCachedRoutes() {
  return (await db.get(ROUTE_INDEX_KEY)) || [];
}

export async function getFullCachedRoute(id) {
  return db.get(routeKey(id));
}

export async function deleteCachedRoute(id) {
  await db.del(routeKey(id));
  const index = (await db.get(ROUTE_INDEX_KEY)) || [];
  await db.set(ROUTE_INDEX_KEY, index.filter(r => r.id !== id));
}

export async function clearAllCache() {
  await db.clearAll();
}

// ─── Per-route tile cache warming ─────────────────────────────────────────────
// Pre-fetch OpenFreeMap vector tiles that cover a route's bbox so the existing
// Workbox `map-tiles` Cache Storage bucket holds them. Going offline later, the
// SW serves those same tile URLs from cache and the map renders. No backend.

const TILE_ZOOM_MIN = 10;
const TILE_ZOOM_MAX = 16;
const TILE_FETCH_CONCURRENCY = 8;
const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

function* tilesInBbox([minLng, minLat, maxLng, maxLat], minZ, maxZ) {
  for (let z = minZ; z <= maxZ; z++) {
    const n = 2 ** z;
    const lng2x = (lng) => Math.floor(((lng + 180) / 360) * n);
    const lat2y = (lat) => {
      const r = (lat * Math.PI) / 180;
      return Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n);
    };
    const x0 = lng2x(minLng), x1 = lng2x(maxLng);
    const y0 = lat2y(maxLat), y1 = lat2y(minLat); // y inverted (north → small y)
    for (let x = Math.min(x0, x1); x <= Math.max(x0, x1); x++) {
      for (let y = Math.min(y0, y1); y <= Math.max(y0, y1); y++) {
        yield { z, x, y };
      }
    }
  }
}

async function resolveTileUrlTemplate() {
  const styleResp = await fetch(STYLE_URL);
  if (!styleResp.ok) throw new Error(`style_fetch_failed_${styleResp.status}`);
  const style = await styleResp.json();
  const src = style?.sources?.openmaptiles;
  if (!src) throw new Error("style_missing_openmaptiles_source");
  if (Array.isArray(src.tiles) && src.tiles[0]) return src.tiles[0];
  if (typeof src.url === "string") {
    const tileJsonResp = await fetch(src.url);
    if (!tileJsonResp.ok) throw new Error(`tilejson_fetch_failed_${tileJsonResp.status}`);
    const tileJson = await tileJsonResp.json();
    if (Array.isArray(tileJson.tiles) && tileJson.tiles[0]) return tileJson.tiles[0];
  }
  throw new Error("tile_url_template_unresolved");
}

function expandTileUrl(template, { z, x, y }) {
  return template.replace("{z}", z).replace("{x}", x).replace("{y}", y);
}

async function runConcurrent(taskFns, concurrency, onTick) {
  const queue = [...taskFns];
  const total = queue.length;
  let done = 0;
  const workers = Array.from({ length: concurrency }, async () => {
    while (queue.length) {
      const fn = queue.shift();
      try { await fn(); } catch { /* swallow — 404s on sparse tiles are normal */ }
      done++;
      onTick?.(done, total);
    }
  });
  await Promise.all(workers);
}

export async function warmRouteTiles(routeId, bbox, onProgress) {
  if (!bbox) throw new Error("missing_bbox");

  const template = await resolveTileUrlTemplate();
  const tiles = [...tilesInBbox(bbox, TILE_ZOOM_MIN, TILE_ZOOM_MAX)];
  const tileCount = tiles.length;

  const tasks = tiles.map(({ z, x, y }) => async () => {
    // Plain GET — Workbox SW intercepts and writes to `map-tiles` bucket.
    await fetch(expandTileUrl(template, { z, x, y }), { cache: "no-store" });
  });

  await runConcurrent(tasks, TILE_FETCH_CONCURRENCY, onProgress);

  const index = (await db.get(ROUTE_INDEX_KEY)) || [];
  const updated = index.map(r =>
    r.id === routeId ? { ...r, tilesDownloaded: true, tileCount } : r
  );
  await db.set(ROUTE_INDEX_KEY, updated);
  return { tileCount };
}

export async function clearRouteTilesFlag(routeId) {
  const index = (await db.get(ROUTE_INDEX_KEY)) || [];
  const updated = index.map(r => {
    if (r.id !== routeId) return r;
    const { tilesDownloaded: _t, tileCount: _c, ...rest } = r;
    return rest;
  });
  await db.set(ROUTE_INDEX_KEY, updated);
}

// One-shot cleanup of any leftover `route_*_pmtiles` blobs from the previous
// PMTiles-based implementation. Safe to call multiple times; no-op if none.
export async function cleanupLegacyPmtiles() {
  const allKeys = await db.keys();
  const legacy = allKeys.filter(k => typeof k === "string" && /_pmtiles$/.test(k));
  await Promise.all(legacy.map(db.del));
  if (legacy.length) {
    const index = (await db.get(ROUTE_INDEX_KEY)) || [];
    const cleaned = index.map(({ pmtilesBytes: _drop, ...rest }) => rest);
    await db.set(ROUTE_INDEX_KEY, cleaned);
  }
}
